import { useQuery } from '@tanstack/react-query';
import type { PortalSubscriptionDto } from '@cpm/shared';
import { Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  statusTone,
} from '../../components/ui.js';

export function PortalSubscriptionsPage() {
  const navigate = useNavigate();
  const subs = useQuery({
    queryKey: ['portal', 'subscriptions'],
    queryFn: () => api.get<PortalSubscriptionDto[]>('/api/portal/subscriptions'),
  });

  return (
    <div>
      <PageHeader
        title="My Subscriptions"
        description="Your hosting subscriptions and the plans they run on."
      />
      <Card>
        {subs.isPending ? (
          <Spinner />
        ) : subs.error ? (
          <div className="p-5">
            <ErrorNote error={subs.error} />
          </div>
        ) : subs.data.length === 0 ? (
          <EmptyState
            icon={<Package className="size-10" />}
            title="No subscriptions yet"
            description="Your hosting provider has not set up a subscription for you yet."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 font-medium">Subscription</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">Health</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Since</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {subs.data.map((sub) => (
                  <tr
                    key={sub.id}
                    className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={() => void navigate(`/portal/subscriptions/${sub.id}`)}
                  >
                    <td className="px-5 py-3 font-medium">{sub.name}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{sub.planName}</td>
                    <td className="px-5 py-3">
                      <Badge tone={statusTone(sub.health)}>{sub.health}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <span className="flex flex-wrap gap-1.5">
                        <Badge tone={statusTone(sub.status)}>{sub.status}</Badge>
                        {sub.overQuota && <Badge tone="red">over quota</Badge>}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {new Date(sub.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
